import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Plus, Megaphone, Image as ImageIcon, PlayCircle, Video, Loader2, Trash2, Info,
} from "lucide-react";
import { MerchantLayout } from "@/pages/merchant/MerchantLayout";
import { api, errorMessage, publicAssetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const KERALA_DISTRICTS = [
    "Thiruvananthapuram", "Kollam", "Pathanamthitta", "Alappuzha", "Kottayam",
    "Idukki", "Ernakulam", "Thrissur", "Palakkad", "Malappuram", "Kozhikode",
    "Wayanad", "Kannur", "Kasaragod",
];
const districtSlug = (n) => n.toLowerCase().replace(/\s+/g, "-");

const FILTERS = [
    { key: "all", label: "All" },
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "expired", label: "Expired" },
];

const AD_TYPES = [
    { key: "hero_banner", label: "Hero Banner", icon: ImageIcon, subtitle: "Full-width slot on the customer home carousel." },
    { key: "strip_ad", label: "Strip Ad", icon: Megaphone, subtitle: "Horizontal strip in the Featured Offers rail." },
    { key: "video", label: "Video Ad", icon: Video, subtitle: "15-second promo video played in the video slot." },
];

const statusPill = (s) => {
    const map = {
        pending: "bg-amber-100 text-amber-700",
        approved: "bg-emerald-100 text-emerald-700",
        rejected: "bg-rose-100 text-rose-700",
        paused: "bg-slate-100 text-slate-600",
        expired: "bg-slate-100 text-slate-500",
    };
    return map[s] || "bg-slate-100 text-slate-600";
};

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (d) => { const t = new Date(); t.setDate(t.getDate() + d); return t.toISOString().slice(0, 10); };

export default function MerchantAdsPage() {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [drawerOpen, setDrawerOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const q = filter === "all" ? "" : `?status=${filter}`;
            const res = await api.get(`/merchant/ads${q}`);
            setItems(res.data.items || []);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const onDelete = async (ad) => {
        if (!window.confirm(`Delete "${ad.headline}"?`)) return;
        try {
            await api.delete(`/merchant/ads/${ad.id}`);
            toast.success("Advertisement deleted");
            load();
        } catch (e) { toast.error(errorMessage(e)); }
    };

    const counts = useMemo(() => {
        const c = { pending: 0, approved: 0, rejected: 0 };
        for (const a of items) if (c[a.status] !== undefined) c[a.status]++;
        return c;
    }, [items]);

    return (
        <MerchantLayout>
            <div className="mx-auto max-w-2xl px-4 py-6" data-testid="merchant-ads-page">
                <header className="mb-4">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="font-heading text-xs font-semibold uppercase tracking-wider text-[var(--brand-orange)]">
                                Advertising
                            </div>
                            <h1 className="mt-1 font-heading text-2xl font-extrabold text-[var(--text-primary)]">
                                My Advertisements
                            </h1>
                            <p className="mt-1 font-body text-sm text-[var(--text-muted)]">
                                Create hero, strip, or video ads. Approved ads go live automatically.
                            </p>
                        </div>
                        <Button
                            className="btn-primary rounded-xl"
                            data-testid="merchant-ad-new-btn"
                            onClick={() => setDrawerOpen(true)}
                        >
                            <Plus size={16} className="mr-1" /> New
                        </Button>
                    </div>
                </header>

                {/* Filter chips */}
                <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="merchant-ads-filters">
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            onClick={() => setFilter(f.key)}
                            data-testid={`merchant-ads-filter-${f.key}`}
                            className={
                                "shrink-0 rounded-full px-3 py-1.5 font-heading text-xs font-semibold " +
                                (filter === f.key
                                    ? "bg-[var(--brand-orange)] text-white"
                                    : "border border-slate-200 bg-white text-[var(--text-primary)]")
                            }
                        >
                            {f.label}
                        </button>
                    ))}
                </div>

                <div className="mb-4 rounded-2xl bg-[#FFF3E6] px-3 py-2 font-body text-xs text-[var(--text-primary)]">
                    <Info size={12} className="mr-1 inline text-[var(--brand-orange)]" />
                    Approved: <b>{counts.approved}</b> · Pending review: <b>{counts.pending}</b>
                    {counts.rejected > 0 && <span> · Rejected: <b>{counts.rejected}</b></span>}
                </div>

                {loading ? (
                    <div className="space-y-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-16 text-center">
                        <Megaphone size={32} className="text-[var(--brand-orange)]" />
                        <div className="font-heading text-lg font-bold text-[var(--text-primary)]">
                            No advertisements yet
                        </div>
                        <p className="max-w-sm font-body text-sm text-[var(--text-muted)]">
                            Start reaching customers across Kerala. Create your first hero, strip or video ad.
                        </p>
                        <Button onClick={() => setDrawerOpen(true)} className="btn-primary rounded-full">
                            <Plus size={16} className="mr-1" /> Create your first ad
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {items.map((a) => (
                            <AdRow key={a.id} ad={a} onDelete={() => onDelete(a)} />
                        ))}
                    </div>
                )}
            </div>

            <AdCreateDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                onCreated={() => { setDrawerOpen(false); load(); }}
            />
        </MerchantLayout>
    );
}

function AdRow({ ad, onDelete }) {
    const preview = ad.artwork_url || ad.video_url;
    const isVideo = ad.ad_type === "video";
    return (
        <div className="card-soft flex flex-col gap-3" data-testid={`merchant-ad-card-${ad.id}`}>
            <div className="flex gap-3">
                <div className="h-20 w-24 shrink-0 overflow-hidden rounded-xl bg-[#FFF3E6]">
                    {preview ? (
                        isVideo ? (
                            <div className="relative flex h-full w-full items-center justify-center bg-black text-white">
                                <PlayCircle size={22} />
                            </div>
                        ) : (
                            <img src={publicAssetUrl(preview)} alt={ad.headline} className="h-full w-full object-cover" />
                        )
                    ) : (
                        <div className="flex h-full w-full items-center justify-center text-[var(--brand-orange)]">
                            <Megaphone size={22} />
                        </div>
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <div className="line-clamp-1 font-heading text-sm font-bold text-[var(--text-primary)]">
                            {ad.headline}
                        </div>
                        <span className={"rounded-full px-2 py-0.5 font-heading text-[10px] font-bold uppercase " + statusPill(ad.status)}>
                            {ad.status}
                        </span>
                    </div>
                    <div className="mt-1 font-body text-xs text-[var(--text-muted)]">
                        {ad.ad_type.replace("_", " ")} · {(ad.district_slugs?.length || 0) === 0 ? "All Kerala" : `${ad.district_slugs.length} districts`}
                    </div>
                    <div className="mt-1 font-body text-[11px] text-[var(--text-muted)]">
                        {ad.start_date} → {ad.expiry_date} · {ad.impressions || 0} impressions · {ad.clicks || 0} clicks
                    </div>
                </div>
            </div>
            {ad.status === "rejected" && ad.rejection_reason && (
                <div className="rounded-xl bg-rose-50 px-3 py-2 font-body text-xs text-rose-700">
                    <b>Rejected:</b> {ad.rejection_reason}
                </div>
            )}
            <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-2">
                <button
                    onClick={onDelete}
                    className="font-heading text-xs font-semibold text-rose-500 hover:text-rose-600"
                >
                    <Trash2 size={12} className="mr-1 inline" /> Delete
                </button>
            </div>
        </div>
    );
}

function AdCreateDrawer({ open, onClose, onCreated }) {
    const [step, setStep] = useState(1); // 1=type, 2=artwork+copy, 3=targeting, 4=schedule, 5=review
    const [ad, setAd] = useState(null); // server-created ad shell
    const [form, setForm] = useState(() => ({
        ad_type: "hero_banner",
        headline: "",
        subline: "",
        cta_url: "",
        district_slugs: [],
        all_kerala: false,
        start_date: today(),
        expiry_date: plusDays(7),
    }));
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (!open) {
            setStep(1);
            setAd(null);
            setForm({
                ad_type: "hero_banner", headline: "", subline: "", cta_url: "",
                district_slugs: [], all_kerala: false,
                start_date: today(), expiry_date: plusDays(7),
            });
        }
    }, [open]);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggleDistrict = (name) => {
        const slug = districtSlug(name);
        setForm((f) => ({
            ...f,
            all_kerala: false,
            district_slugs: f.district_slugs.includes(slug)
                ? f.district_slugs.filter((d) => d !== slug)
                : [...f.district_slugs, slug],
        }));
    };

    const submit = async () => {
        if (saving) return;
        if (!form.headline.trim()) { toast.error("Headline is required"); return; }
        if (form.expiry_date < form.start_date) { toast.error("Expiry must be on/after start"); return; }
        setSaving(true);
        try {
            const payload = {
                ad_type: form.ad_type,
                headline: form.headline.trim(),
                subline: form.subline?.trim() || null,
                cta_url: form.cta_url?.trim() || null,
                district_slugs: form.all_kerala ? [] : form.district_slugs,
                start_date: form.start_date,
                expiry_date: form.expiry_date,
            };
            const res = await api.post("/merchant/ads", payload);
            toast.success("Advertisement submitted for review");
            onCreated?.(res.data);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally { setSaving(false); }
    };

    const uploadArtworkForCreatedAd = async (adId, file) => {
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await api.post(`/merchant/ads/${adId}/artwork`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setAd(res.data);
            toast.success("Artwork uploaded");
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setUploading(false); }
    };

    const preCreateForUpload = async () => {
        // If user picks artwork BEFORE submit, we need a doc first to attach to.
        if (ad?.id) return ad.id;
        const payload = {
            ad_type: form.ad_type,
            headline: form.headline.trim() || "Untitled draft",
            district_slugs: form.all_kerala ? [] : form.district_slugs,
            start_date: form.start_date,
            expiry_date: form.expiry_date,
        };
        const res = await api.post("/merchant/ads", payload);
        setAd(res.data);
        return res.data.id;
    };

    const onFileChange = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        try {
            const id = await preCreateForUpload();
            await uploadArtworkForCreatedAd(id, f);
        } catch (err) { toast.error(errorMessage(err)); }
    };

    return (
        <Sheet open={open} onOpenChange={(v) => (!v && !saving) && onClose?.()}>
            <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="merchant-ad-drawer">
                <SheetHeader className="text-left">
                    <SheetTitle className="font-heading text-xl font-extrabold">New advertisement</SheetTitle>
                    <SheetDescription className="font-body text-xs text-[var(--text-muted)]">
                        Step {step} of 5
                    </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-6">
                    {step === 1 && (
                        <div className="space-y-3" data-testid="merchant-ad-step-type">
                            <div className="font-heading text-sm font-bold">Choose ad type</div>
                            <div className="grid grid-cols-1 gap-3">
                                {AD_TYPES.map((t) => {
                                    const Icon = t.icon;
                                    const active = form.ad_type === t.key;
                                    return (
                                        <button
                                            key={t.key}
                                            data-testid={`merchant-ad-type-${t.key.replace("_ad", "")}`}
                                            onClick={() => setField("ad_type", t.key)}
                                            className={
                                                "flex items-center gap-3 rounded-2xl border p-4 text-left transition " +
                                                (active
                                                    ? "border-[var(--brand-orange)] bg-[#FFF3E6]"
                                                    : "border-slate-200 hover:border-[var(--brand-orange)]")
                                            }
                                        >
                                            <div className={"flex h-12 w-12 items-center justify-center rounded-xl " + (active ? "bg-[var(--brand-orange)] text-white" : "bg-[#FFF3E6] text-[var(--brand-orange)]")}>
                                                <Icon size={22} />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="font-heading text-sm font-bold text-[var(--text-primary)]">{t.label}</div>
                                                <div className="font-body text-xs text-[var(--text-muted)]">{t.subtitle}</div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4" data-testid="merchant-ad-step-artwork">
                            <FormRow label="Headline">
                                <Input value={form.headline} onChange={(e) => setField("headline", e.target.value)} placeholder="Grand Weekend Sale — 40% off" data-testid="merchant-ad-headline" />
                            </FormRow>
                            <FormRow label="Subline">
                                <Input value={form.subline} onChange={(e) => setField("subline", e.target.value)} placeholder="Ends Sunday · All stores" />
                            </FormRow>
                            <FormRow label="Call-to-action URL (optional)">
                                <Input value={form.cta_url} onChange={(e) => setField("cta_url", e.target.value)} placeholder="https://…" />
                            </FormRow>

                            <FormRow label={form.ad_type === "video" ? "Video (mp4, max 30MB)" : "Artwork (jpg/png/webp, max 4MB)"}>
                                <label className={"flex cursor-pointer flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 px-4 py-6 text-center hover:border-[var(--brand-orange)] " + (uploading ? "opacity-60" : "")}>
                                    {uploading ? (
                                        <Loader2 size={22} className="animate-spin text-[var(--brand-orange)]" />
                                    ) : (ad?.artwork_url || ad?.video_url) ? (
                                        <div className="w-full space-y-2">
                                            {ad.artwork_url && <img src={publicAssetUrl(ad.artwork_url)} alt="preview" className="mx-auto max-h-40 rounded-xl object-contain" />}
                                            {ad.video_url && <div className="font-body text-xs text-[var(--text-muted)]">Video uploaded ✓</div>}
                                            <div className="font-body text-xs text-[var(--brand-orange)]">Replace file</div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF3E6] text-[var(--brand-orange)]"><ImageIcon size={18} /></div>
                                            <div className="font-heading text-sm font-semibold text-[var(--text-primary)]">Click to upload</div>
                                            <div className="font-body text-xs text-[var(--text-muted)]">
                                                {form.ad_type === "video" ? "15-second video recommended" : "Recommended 1200×600"}
                                            </div>
                                        </>
                                    )}
                                    <input
                                        type="file"
                                        accept={form.ad_type === "video" ? "video/mp4,video/quicktime,video/webm" : "image/png,image/jpeg,image/webp"}
                                        className="sr-only"
                                        onChange={onFileChange}
                                        data-testid="merchant-ad-artwork-upload"
                                        disabled={uploading}
                                    />
                                </label>
                            </FormRow>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-3" data-testid="merchant-ad-step-targeting">
                            <div className="font-heading text-sm font-bold">Target districts</div>
                            <button
                                onClick={() => setField("all_kerala", !form.all_kerala) || setField("district_slugs", [])}
                                data-testid="merchant-ad-district-all-toggle"
                                className={"w-full rounded-2xl border px-4 py-3 text-left font-heading text-sm font-semibold transition " + (form.all_kerala ? "border-[var(--brand-orange)] bg-[#FFF3E6] text-[var(--brand-orange)]" : "border-slate-200")}
                            >
                                All Kerala {form.all_kerala && "✓"}
                            </button>
                            <div className="grid grid-cols-2 gap-2" data-testid="merchant-ad-district-picker">
                                {KERALA_DISTRICTS.map((d) => {
                                    const slug = districtSlug(d);
                                    const on = !form.all_kerala && form.district_slugs.includes(slug);
                                    return (
                                        <button
                                            key={d}
                                            onClick={() => toggleDistrict(d)}
                                            disabled={form.all_kerala}
                                            data-testid={`merchant-ad-district-${slug}`}
                                            className={"rounded-xl border px-3 py-2 text-left font-body text-xs transition " + (on ? "border-[var(--brand-orange)] bg-[#FFF3E6] text-[var(--brand-orange)] font-semibold" : "border-slate-200 hover:border-[var(--brand-orange)] " + (form.all_kerala ? "opacity-40" : ""))}
                                        >
                                            {d}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="space-y-4" data-testid="merchant-ad-step-schedule">
                            <FormRow label="Start date">
                                <Input type="date" value={form.start_date} min={today()} onChange={(e) => setField("start_date", e.target.value)} data-testid="merchant-ad-date-start" />
                            </FormRow>
                            <FormRow label="Expiry date">
                                <Input type="date" value={form.expiry_date} min={form.start_date} onChange={(e) => setField("expiry_date", e.target.value)} data-testid="merchant-ad-date-end" />
                            </FormRow>
                            <div className="rounded-xl bg-[#FFF3E6] px-3 py-2 font-body text-xs text-[var(--text-primary)]">
                                Runs for {Math.max(0, Math.ceil((new Date(form.expiry_date) - new Date(form.start_date)) / 86400000))} days.
                            </div>
                        </div>
                    )}

                    {step === 5 && (
                        <div className="space-y-4" data-testid="merchant-ad-step-review">
                            <div className="rounded-2xl bg-slate-50 p-4">
                                <div className="font-heading text-xs uppercase tracking-wider text-[var(--brand-orange)]">Preview</div>
                                <div className="mt-2 font-heading text-base font-bold text-[var(--text-primary)]">{form.headline || "—"}</div>
                                {form.subline && <div className="mt-1 font-body text-sm text-[var(--text-muted)]">{form.subline}</div>}
                                <div className="mt-3 grid grid-cols-2 gap-2 font-body text-xs">
                                    <div><b>Type:</b> {form.ad_type.replace("_", " ")}</div>
                                    <div><b>Runs:</b> {form.start_date} → {form.expiry_date}</div>
                                    <div className="col-span-2"><b>Districts:</b> {form.all_kerala ? "All Kerala" : (form.district_slugs.length ? form.district_slugs.join(", ") : "None selected")}</div>
                                </div>
                            </div>
                            <div className="rounded-xl bg-amber-50 px-3 py-2 font-body text-xs text-amber-800">
                                Your ad is submitted for review after clicking submit. Approved ads go live automatically.
                            </div>
                        </div>
                    )}
                </div>

                <div className="sticky bottom-0 -mx-6 mt-6 flex items-center justify-between border-t border-slate-100 bg-white px-6 py-4">
                    <button
                        onClick={() => step > 1 ? setStep((s) => s - 1) : onClose?.()}
                        className="font-heading text-sm font-semibold text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                        data-testid="merchant-ad-back-btn"
                    >
                        {step === 1 ? "Cancel" : "Back"}
                    </button>
                    {step < 5 ? (
                        <Button
                            onClick={() => setStep((s) => Math.min(5, s + 1))}
                            className="btn-primary rounded-xl"
                            data-testid="merchant-ad-next-btn"
                            disabled={step === 2 && !form.headline.trim()}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button
                            onClick={submit}
                            disabled={saving}
                            className="btn-primary rounded-xl"
                            data-testid="merchant-ad-submit-btn"
                        >
                            {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
                            Submit for Review
                        </Button>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}

function FormRow({ label, children }) {
    return (
        <label className="flex flex-col gap-1">
            <span className="font-heading text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">{label}</span>
            {children}
        </label>
    );
}
